import { useState } from "react"
import { useHistory } from "react-router-dom"
import { adminLookupUserByEmail, CardView } from "shared"
import type { LookupUserResult, UserFromDb } from "shared"
import { Button } from "shared/src/components/shadcn/components/atoms/buttons"
import { Input } from "shared/src/components/shadcn/components/ui/input"
import { UserBadge } from "../components/UserBadge"

function teamUrlForResult(result: LookupUserResult): string {
	const base = `/client/${result.clientId}/team`
	return result.userId ? `${base}?userId=${result.userId}` : base
}

function toUserFromDb(result: LookupUserResult): UserFromDb {
	return {
		pk: `cli#${result.clientId}`,
		sk: "",
		miscData: "",
		userId: result.userId ?? undefined,
		externalId: result.externalId ?? undefined,
		evoRef: result.evoRef ?? undefined,
	} as UserFromDb
}

export function UserLookup() {
	const history = useHistory()
	const [email, setEmail] = useState("")
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [results, setResults] = useState<LookupUserResult[]>([])

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault()
		const trimmed = email.trim()
		if (!trimmed) return

		setError(null)
		setResults([])
		setLoading(true)
		try {
			const response = await adminLookupUserByEmail({ email: trimmed })
			if (response.results.length === 1) {
				history.push(teamUrlForResult(response.results[0]))
			} else {
				setResults(response.results)
			}
		} catch (err: unknown) {
			const status = (err as { response?: { status?: number } })?.response?.status
			if (status === 404) {
				setError("A user with this email address does not exist")
			} else {
				setError("Something went wrong. Please try again.")
			}
		} finally {
			setLoading(false)
		}
	}

	return (
		<div style={{ maxWidth: "90%", margin: "auto", gap: 16, display: "flex", flexDirection: "column" }}>
			<h1 className="bs-h1">User Lookup</h1>
			<CardView>
				<h4 className="bs-h4">Search for a user by email address</h4>
				<form onSubmit={handleSubmit} style={{ display: "flex", justifyContent: "space-around", margin: "50px 50px" }}>
					<div style={{ display: "flex", justifyContent: "space-around", alignItems: "center" }}>
						<div style={{ paddingRight: 15 }}>Email Address:</div>
						<Input
							type="email"
							placeholder="user@example.com"
							value={email}
							onChange={(e) => {
								setEmail(e.target.value)
								setError(null)
								setResults([])
							}}
							style={{ width: 600 }}
						/>
					</div>
					<Button type="submit" disabled={!email.trim()} loading={loading}>
						Search
					</Button>
				</form>
				{error && <div style={{ color: "red", textAlign: "center", marginBottom: 20 }}>{error}</div>}
				{results.length > 1 && (
					<div style={{ margin: "0 50px 20px" }}>
						<p style={{ marginBottom: 12 }}>Found {results.length} accounts with this email. Select one to view:</p>
						<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
							{results.map((result) => (
								<button
									type="button"
									key={result.clientId}
									onClick={() => history.push(teamUrlForResult(result))}
									style={{
										display: "flex",
										alignItems: "center",
										gap: 12,
										padding: "12px 16px",
										border: "1px solid #e2e8f0",
										borderRadius: 8,
										background: "transparent",
										textAlign: "left",
										font: "inherit",
										width: "100%",
										cursor: "pointer",
										transition: "background-color 0.15s",
									}}
									onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f7fafc")}
									onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}>
									<div style={{ flex: 1 }}>
										<div style={{ fontWeight: 600 }}>{result.clientName}</div>
										{result.userName && <div style={{ fontSize: 14, color: "#64748b" }}>{result.userName}</div>}
									</div>
									<UserBadge user={toUserFromDb(result)} />
								</button>
							))}
						</div>
					</div>
				)}
			</CardView>
		</div>
	)
}
